/**
 * F-recados-equipe (CEO 2026-10-28) — "Recados": o Administrador (ou Super
 * Admin) escreve para a equipe do Gestion e/ou técnicos, com agendamento
 * único ("mostrar a partir de") e validade. Quem recebe vê em pop-up ao
 * entrar; "Ciente" fecha até a próxima entrada, "Feito" encerra.
 */
import { useState } from "react";
import { ErpShell } from "../components/layout/ErpShell.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none";
type TargetKind = "user" | "role" | "company" | "technicians" | "all";
type TargetRole = "admin" | "user" | "viewer" | "manager" | "technician";

/** `datetime-local` → Date (hora local do navegador = hora de Paris no escritório). */
function dataLocal(v: string): Date | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

export default function TeamNotices() {
  const t = useT();
  const { user } = useAuth();
  const utils = trpc.useUtils();
  const companiesQuery = trpc.companies.list.useQuery();
  // Só empresas onde ele é Administrador (o Super Admin, todas).
  const adminCompanies = (companiesQuery.data ?? []).filter(
    (c) =>
      user?.isSuperAdmin ||
      user?.memberships.some((m) => m.active && m.companyId === c.id && m.role === "admin"),
  );
  const [companyId, setCompanyId] = useSelectedCompany(adminCompanies);
  const [msg, setMsg] = useState("");
  const [kind, setKind] = useState<TargetKind>("company");
  const [targetUserId, setTargetUserId] = useState<number | "">("");
  const [targetRole, setTargetRole] = useState<TargetRole>("user");
  const [showFrom, setShowFrom] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  // F-recados-repetir (CEO 2026-10-29) — dias da semana (ISO 1 = seg … 7 = dom).
  const [repetir, setRepetir] = useState(false);
  const [dias, setDias] = useState<number[]>([1, 3, 5]);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const usersQ = trpc.users.listByCompany.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId != null && kind === "user" },
  );
  const listQ = trpc.erpNotices.list.useQuery(
    { companyId: companyId ?? 0 },
    { enabled: companyId != null },
  );
  const createMut = trpc.erpNotices.create.useMutation({
    onSuccess: () => {
      setMsg("");
      setShowFrom("");
      setExpiresAt("");
      setRepetir(false);
      setOkMsg(t("teamNotices.sent"));
      void utils.erpNotices.list.invalidate();
      void utils.erpNotices.mine.invalidate();
    },
  });
  const cancelMut = trpc.erpNotices.cancel.useMutation({
    onSuccess: () => {
      void utils.erpNotices.list.invalidate();
      void utils.erpNotices.mine.invalidate();
    },
  });

  const fmt = (d: string | Date) =>
    new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
  const roleLabel = (r: string) => t(`teamNotices.role_${r}` as Parameters<typeof t>[0]);
  const targetLabel = (r: {
    targetKind: TargetKind;
    targetRole: string | null;
    targetUserName: string | null;
  }) => {
    switch (r.targetKind) {
      case "user":
        return `${t("teamNotices.targetUser")}: ${r.targetUserName ?? "?"}`;
      case "role":
        return `${t("teamNotices.targetRole")}: ${r.targetRole ? roleLabel(r.targetRole) : "?"}`;
      case "company":
        return t("teamNotices.targetCompany");
      case "technicians":
        return t("teamNotices.targetTechnicians");
      default:
        return t("teamNotices.targetAll");
    }
  };
  const statusOf = (r: {
    showFrom: Date | string;
    expiresAt: Date | string | null;
    cancelledAt: Date | string | null;
  }) => {
    const now = Date.now();
    if (r.cancelledAt) return { key: "statusCancelled", cls: "badge b-mut" } as const;
    if (r.expiresAt && new Date(r.expiresAt).getTime() <= now)
      return { key: "statusExpired", cls: "badge b-mut" } as const;
    if (new Date(r.showFrom).getTime() > now)
      return { key: "statusScheduled", cls: "badge b-info" } as const;
    return { key: "statusActive", cls: "badge b-ok" } as const;
  };
  const podeEnviar =
    companyId != null &&
    msg.trim().length >= 5 &&
    (kind !== "user" || targetUserId !== "") &&
    (!repetir || dias.length > 0) &&
    !createMut.isPending;

  return (
    <ErpShell>
      <div className="space-y-4">
        <div>
          <h1 className="text-xl font-semibold">📣 {t("teamNotices.title")}</h1>
          <p className="text-xs text-text-muted">{t("teamNotices.hint")}</p>
        </div>
        {adminCompanies.length === 0 ? (
          <p className="text-sm text-amber-300">{t("teamNotices.noAdmin")}</p>
        ) : (
          <>
            <section className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900 p-4">
              <div className="flex flex-wrap items-end gap-3">
                <label className="block text-xs">
                  <span className="mb-1 block text-text-muted">
                    {t("teamNotices.companyLabel")}
                  </span>
                  <select
                    value={companyId ?? ""}
                    onChange={(e) => setCompanyId(Number(e.target.value))}
                    className={inputClass + " w-64"}
                    data-testid="tn-company"
                  >
                    {adminCompanies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs">
                  <span className="mb-1 block text-text-muted">{t("teamNotices.targetLabel")}</span>
                  <select
                    value={kind}
                    onChange={(e) => setKind(e.target.value as TargetKind)}
                    className={inputClass + " w-56"}
                    data-testid="tn-kind"
                  >
                    <option value="company">{t("teamNotices.targetCompany")}</option>
                    <option value="technicians">{t("teamNotices.targetTechnicians")}</option>
                    <option value="all">{t("teamNotices.targetAll")}</option>
                    <option value="role">{t("teamNotices.targetRole")}</option>
                    <option value="user">{t("teamNotices.targetUser")}</option>
                  </select>
                </label>
                {kind === "user" && (
                  <label className="block text-xs">
                    <span className="mb-1 block text-text-muted">{t("teamNotices.userLabel")}</span>
                    <select
                      value={targetUserId}
                      onChange={(e) =>
                        setTargetUserId(e.target.value ? Number(e.target.value) : "")
                      }
                      className={inputClass + " w-64"}
                      data-testid="tn-user"
                    >
                      <option value="">—</option>
                      {(usersQ.data ?? [])
                        .filter((u) => u.membershipActive && u.active)
                        .map((u) => (
                          <option key={u.userId} value={u.userId}>
                            {u.name} · {roleLabel(u.role)}
                          </option>
                        ))}
                    </select>
                  </label>
                )}
                {kind === "role" && (
                  <label className="block text-xs">
                    <span className="mb-1 block text-text-muted">{t("teamNotices.roleLabel")}</span>
                    <select
                      value={targetRole}
                      onChange={(e) => setTargetRole(e.target.value as TargetRole)}
                      className={inputClass + " w-56"}
                      data-testid="tn-role"
                    >
                      {(["admin", "user", "viewer", "manager", "technician"] as const).map((r) => (
                        <option key={r} value={r}>
                          {roleLabel(r)}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <textarea
                value={msg}
                onChange={(e) => {
                  setMsg(e.target.value);
                  setOkMsg(null);
                }}
                rows={4}
                maxLength={2000}
                className={inputClass + " w-full"}
                placeholder={t("teamNotices.messagePlaceholder")}
                data-testid="tn-text"
              />
              <div className="flex flex-wrap items-end gap-3">
                <label className="block text-xs">
                  <span className="mb-1 block text-text-muted">
                    {t("teamNotices.showFromLabel")}
                  </span>
                  <input
                    type="datetime-local"
                    value={showFrom}
                    onChange={(e) => setShowFrom(e.target.value)}
                    className={inputClass}
                    data-testid="tn-from"
                  />
                  <span className="mt-1 block text-[11px] text-text-muted">
                    {t("teamNotices.showFromHint")}
                  </span>
                </label>
                <label className="block text-xs">
                  <span className="mb-1 block text-text-muted">
                    {t("teamNotices.expiresLabel")}
                  </span>
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className={inputClass}
                    data-testid="tn-until"
                  />
                  <span className="mt-1 block text-[11px] text-text-muted">
                    {t("teamNotices.expiresHint")}
                  </span>
                </label>
                <div className="block text-xs">
                  <label className="mb-1 flex items-center gap-1.5 text-text-muted">
                    <input
                      type="checkbox"
                      checked={repetir}
                      onChange={(e) => setRepetir(e.target.checked)}
                      data-testid="tn-repeat"
                    />
                    {t("teamNotices.repeatLabel")}
                  </label>
                  {repetir && (
                    <div
                      className="flex flex-wrap gap-1"
                      role="group"
                      aria-label={t("teamNotices.repeatLabel")}
                    >
                      {[1, 2, 3, 4, 5, 6, 7].map((dia) => {
                        const on = dias.includes(dia);
                        return (
                          <button
                            key={dia}
                            type="button"
                            aria-pressed={on}
                            onClick={() =>
                              setDias((v) => (on ? v.filter((x) => x !== dia) : [...v, dia]))
                            }
                            className={`h-8 min-w-8 rounded-md border px-2 text-xs font-semibold ${
                              on
                                ? "border-lime bg-lime text-on-lime"
                                : "border-neutral-700 text-neutral-300"
                            }`}
                            data-testid={`tn-day-${dia}`}
                          >
                            {t(`teamNotices.dayLong${dia}` as Parameters<typeof t>[0])}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {repetir && (
                    <span className="mt-1 block text-[11px] text-text-muted">
                      {t("teamNotices.repeatHint")}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  disabled={!podeEnviar}
                  onClick={() =>
                    createMut.mutate({
                      companyId: companyId!,
                      message: msg.trim(),
                      targetKind: kind,
                      targetUserId:
                        kind === "user" && targetUserId !== "" ? targetUserId : undefined,
                      targetRole: kind === "role" ? targetRole : undefined,
                      showFrom: dataLocal(showFrom),
                      expiresAt: dataLocal(expiresAt),
                      repeatWeekdays: repetir && dias.length > 0 ? dias : undefined,
                    })
                  }
                  className="rounded-md bg-lime px-4 py-2 text-sm font-semibold text-on-lime disabled:opacity-40"
                  data-testid="tn-send"
                >
                  📣 {createMut.isPending ? "…" : t("teamNotices.sendBtn")}
                </button>
              </div>
              {createMut.error && <p className="text-xs text-red-400">{createMut.error.message}</p>}
              {okMsg && <p className="text-xs text-lime">{okMsg}</p>}
            </section>

            <section className="space-y-2">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-text-muted">
                {t("teamNotices.listTitle")}
              </h2>
              <div className="divide-y divide-neutral-800 rounded-md border border-neutral-800">
                {(listQ.data?.rows ?? []).length === 0 && (
                  <p className="px-3 py-3 text-sm text-text-muted">{t("teamNotices.listEmpty")}</p>
                )}
                {(listQ.data?.rows ?? []).map((r) => {
                  const st = statusOf(r);
                  return (
                    <div key={r.id} className="space-y-1 px-3 py-2.5" data-testid="tn-row">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-text-muted">
                        <span className={st.cls}>{t(`teamNotices.${st.key}`)}</span>
                        <span>{targetLabel(r)}</span>
                        <span>· {fmt(r.showFrom)}</span>
                        {r.expiresAt && <span>→ {fmt(r.expiresAt)}</span>}
                        {r.info && (
                          <span className="badge b-mut" data-testid="tn-info-badge">
                            ℹ️ {t("teamNotices.infoBadge")}
                          </span>
                        )}
                        {r.repeatWeekdays && r.repeatWeekdays.length > 0 && (
                          <span className="badge b-info" data-testid="tn-repeat-badge">
                            ↻ {t("teamNotices.repeatEvery")}{" "}
                            {r.repeatWeekdays
                              .map((d) => t(`teamNotices.dayLong${d}` as Parameters<typeof t>[0]))
                              .join(", ")}
                          </span>
                        )}
                        {r.createdByName && <span>· {r.createdByName}</span>}
                        <span>
                          · {t("teamNotices.readBy")} {r.readCount} · {t("teamNotices.doneBy")}{" "}
                          {r.repeatWeekdays && r.repeatWeekdays.length > 0
                            ? `${r.doneToday} (${t("teamNotices.today")})`
                            : r.doneCount}
                        </span>
                        {!r.cancelledAt && st.key !== "statusExpired" && (
                          <button
                            type="button"
                            disabled={cancelMut.isPending}
                            onClick={() => cancelMut.mutate({ companyId: companyId!, id: r.id })}
                            className="rounded border border-neutral-700 px-2 py-0.5 text-[11px]"
                            data-testid="tn-cancel"
                          >
                            {t("teamNotices.cancelBtn")}
                          </button>
                        )}
                      </div>
                      <p className="whitespace-pre-line text-sm">{r.message}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        )}
      </div>
    </ErpShell>
  );
}
